// ======================================================================
// File: RFQViewDrawer.jsx
// Module: PPE → Procurement → RFQ
//
// Purpose:
// - View RFQ details in read-only mode
// - Show header information
// - Show RFQ lines
// - Show mapped vendors with enhanced status UI
// - Same drawer pattern as PurchaseRequestViewDrawer
// ======================================================================

import React, { useEffect, useState } from "react";

import rfqService from "../services/rfqService";
import {
  formatDate,
  formatQuantity
} from "../../../../../../utils/formatUtils";

import "../rfq-ui.css";

export default function RFQViewDrawer({
  open,
  onClose,
  rfqId
}) {

  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  // ==============================================================
  // HELPER FUNCTIONS
  // ==============================================================

  const getStatusBadge = (status) => {
    switch (status) {
      case "RESPONDED":
        return (
          <span style={{ color: "green", fontWeight: "bold" }}>
            RESPONDED
          </span>
        );

      case "INVITED":
        return (
          <span style={{ color: "orange", fontWeight: "bold" }}>
            INVITED
          </span>
        );

      default:
        return <span>{status}</span>;
    }
  };

  

  // ==============================================================
  // LOAD RFQ DETAIL
  // ==============================================================

  useEffect(() => {
    if (open && rfqId) {
      loadDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rfqId]);

  const loadDetail = async () => {
    try {
      setLoading(true);

      const res = await rfqService.getRFQById(rfqId);

      setDetail(res?.data?.data || null);

    } catch (err) {
      console.error("Failed to load RFQ detail", err);
    } finally {
      setLoading(false);
    }
  };

  // ==============================================================
  // RENDER
  // ==============================================================

  if (!open) return null;

  return (
    <div className="pr-drawer open">

      {/* HEADER */}
      <div className="pr-drawer-header">

  <div>
    <h3>{detail?.rfq_no || "RFQ Details"}</h3>

    <div className="rfq-header-subtitle">
      Purchase Request :
      {detail?.pr_no || detail?.pr_id || "-"}
    </div>
  </div>

        <button
          className="btn-secondary"
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      {/* BODY */}
      <div className="pr-drawer-body">

        {loading && <div>Loading...</div>}

        {!loading && detail && (
          <>

            {/* BASIC INFO */}
            <div className="form-grid">

              <div className="form-field">
                <label>RFQ No</label>
                <div>{detail.rfq_no}</div>
              </div>

              <div className="form-field">
                <label>RFQ Date</label>
                <div>{formatDate(detail.rfq_date)}</div>
              </div>

              <div className="form-field">
                <label>Quote Due Date</label>
                <div>{formatDate(detail.quote_due_date)}</div>
              </div>

              <div className="form-field">
                <label>Validity Date</label>
                <div>
                  {detail.validity_date
                    ? formatDate(detail.validity_date)
                    : "-"}
                </div>
              </div>

              <div className="form-field">
              <label>Status</label>
              <div>
                <div>
                  <span
                    className={`rfq-status-badge rfq-status-${(
                      detail.status_code ||
                      detail.status_name ||
                      "draft"
                    )
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    {detail.status_name || detail.status_code}
                  </span>
                </div>
              </div>
            </div>

            


              <div className="form-field full">
                <label>Remarks</label>
                <div>{detail.remarks || "-"}</div>
              </div>

            </div>

            {/* LINE ITEMS */}
            <div className="section-header">
              <h4>RFQ Materials</h4>
            </div>

            <div className="rfq-table-wrapper">
              <table className="rfq-table">

                <thead>
                  <tr>
                  <th>#</th>
                  <th>Material</th>
                    <th>Qty</th>
                    <th>UOM</th>
                    <th>Required Date</th>
                  </tr>
                </thead>

                <tbody>

                  {detail.lines?.length === 0 && (
                    <tr>
                      <td colSpan="5">
                        No line items
                      </td>
                    </tr>
                  )}

                  {detail.lines?.map((l, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{l.material_name || l.material_id}</td>
                      <td>{formatQuantity(l.qty)}</td>
                      <td>{l.uom}</td>
                      <td>{formatDate(l.required_date)}</td>
                    </tr>
                  ))}

                </tbody>
              </table>
            </div>

            {/* VENDORS */}

            {/* VENDORS */}
            {detail.finalized_vendor_name && (
              <div className="rfq-finalized-vendor">
                Selected Vendor :
                <strong>
                  {" "}{detail.finalized_vendor_name}
                </strong>
              </div>
            )}      
            <div className="section-header">
              <h4>Mapped Vendors</h4>
            </div>

            <div className="rfq-table-wrapper">
              <table className="rfq-table">

                <thead>
                  <tr>
                    <th>#</th>
                    <th>Vendor Name</th>
                    <th>Status</th>
                    <th>Invited On</th>
                    <th>Responded On</th>
                  </tr>
                </thead>

                <tbody>

                  {detail.vendors?.length === 0 && (
                    <tr>
                      <td colSpan="5">
                        No vendors attached
                      </td>
                    </tr>
                  )}

                  {detail.vendors?.map((v, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>

                      <td>{v.vendor_name || v.vendor_id}</td>

                      <td>
                        {v.is_finalized ? (
                          <span className="rfq-status-badge rfq-status-finalized">
                            FINALIZED
                          </span>
                        ) : (
                          getStatusBadge(v.response_status)
                        )}
                      </td>

                      <td>{formatDate(v.invitation_sent_on)}</td>

                      <td>{formatDate(v.responded_at)}</td>
                    </tr>
                  ))}

                </tbody>
              </table>
            </div>

            </>
            )}

            </div>

            {/* FOOTER */}
            <div className="pr-drawer-footer">
              <button
                className="btn-secondary"
                onClick={onClose}
              >
                Close
              </button>
            </div>

            </div>
            );
            }
